import React, { useState, useEffect } from "react";
import { LogOut, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  CircularProgress,
  Snackbar,
  Alert,
  Avatar,
  Autocomplete,
  Box,
  IconButton,
  Typography,
  Container,
} from "@mui/material";
import { ArrowBack, Lock } from "@mui/icons-material";
import PatientProfile from "../components/PatientProfile";
import dayjs from "dayjs";
import Logo from "../assets/images/commaLogo.png";
import CommaPatientImport from "../components/CommaPatientImport";

const API_BASE_URL = "https://genz-staging.feeltiptop.com";

const SpeechDashboard = () => {
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [doctorInfo, setDoctorInfo] = useState(null);
  const [alertState, setAlertState] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [newPatient, setNewPatient] = useState({
    firstName: "",
    lastName: "",
    ethnicity: "",
    gender: "M",
    dateOfBirth: "",
    email: "",
    phoneNumber: "",
  });
  const [selectedPatientId, setSelectedPatientId] = useState(null);
  const [verifyingDoctor, setVerifyingDoctor] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);

  const getAuthHeaders = () => ({
    headers: {
      Authorization: `Bearer ${localStorage.getItem("authToken")}`,
      "Content-Type": "application/json",
    },
  });

  useEffect(() => {
    verifyDoctorAccess();
  }, []);

  const verifyDoctorAccess = async () => {
    if (!localStorage.getItem("authToken")) {
      setVerifyingDoctor(false);
      return;
    }

    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/doctor/info`,
        getAuthHeaders(),
      );

      if (response.data.success) {
        setDoctorInfo(response.data.doctor);
        setIsAuthorized(true);
        fetchPatients();
      }
    } catch (error) {
      console.error("Error verifying doctor status:", error);
    } finally {
      setVerifyingDoctor(false);
    }
  };

  const fetchPatients = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/doctor/patients`,
        getAuthHeaders(),
      );
      if (response.data.success) {
        setPatients(response.data.patients);
      }
    } catch (error) {
      console.error("Error fetching patients:", error);
      setAlertState({
        open: true,
        message: "Failed to fetch patients",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Alias for bulk import refresh
  const refreshPatients = fetchPatients;

  const handleSearch = (e) => setSearchTerm(e.target.value);

  const filteredPatients = patients.filter((p) =>
    `${p.fullName}`.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const handleAddPatient = () => {
    setOpenDialog(true);
    setNewPatient({
      firstName: "",
      lastName: "",
      ethnicity: "",
      gender: "M",
      dateOfBirth: "",
      email: "",
      phoneNumber: "",
    });
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setNewPatient({
      firstName: "",
      lastName: "",
      ethnicity: "",
      gender: "M",
      dateOfBirth: "",
      email: "",
      phoneNumber: "",
    });
  };

  const handleSubmitPatient = async () => {
    const {
      firstName,
      lastName,
      ethnicity,
      gender,
      dateOfBirth,
      email,
      phoneNumber,
    } = newPatient;
    if (
      !firstName ||
      !lastName ||
      !ethnicity ||
      !gender ||
      !dateOfBirth ||
      !email
    ) {
      setAlertState({
        open: true,
        message: "Please fill all fieldss",
        severity: "error",
      });
      return;
    }
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/patient/onboard`,
        {
          firstName,
          lastName,
          ethnicity,
          gender,
          dateOfBirth: dayjs(dateOfBirth).format("YYYY-MM-DD"),
          email,
          phoneNumber,
        },
        getAuthHeaders(),
      );
      if (response.data.success) {
        setAlertState({
          open: true,
          message: "Patient added successfully!",
          severity: "success",
        });
        handleCloseDialog();
        fetchPatients();
      } else {
        setAlertState({
          open: true,
          message: response.data.message || "Failed to add patient",
          severity: "error",
        });
      }
    } catch (error) {
      setAlertState({
        open: true,
        message: error.response?.data?.message || "Failed to add patient",
        severity: "error",
      });
    }
  };

  const handleViewProfile = (id) => {
    setSelectedPatientId(id);
  };

  const handleBackToList = () => {
    setSelectedPatientId(null);
  };

  const handleLogout = () => {
    localStorage.removeItem("authToken");
    navigate("/login");
  };

  if (verifyingDoctor) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <CircularProgress />
      </div>
    );
  }

  if (!isAuthorized) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            minHeight: "80vh",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Lock sx={{ fontSize: 64, color: "text.secondary" }} />
          <Typography variant="h4" component="h1" gutterBottom>
            Restricted Page
          </Typography>
          <Typography variant="body1" color="text.secondary" align="center">
            This page is only accessible to authorized medical professionals.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate("/")}
            sx={{ mt: 2 }}
          >
            Return to Home
          </Button>
        </Box>
      </Container>
    );
  }

  if (selectedPatientId) {
    // Animated back button that slides out from behind the logo on hover
    return (
      <PatientProfile
        patientId={selectedPatientId}
        doctorInformation={doctorInfo}
        onBack={handleBackToList}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navbar */}
      <nav className="flex items-center justify-between bg-white px-6 py-4 shadow">
        <div
          className="duration-400 flex items-center transition-all ease-in-out"
          style={{}}
        >
          {/* Animated Back Button */}
          <button
            onClick={handleBackToList}
            className="duration-400 pointer-events-none z-10 -translate-x-14 transform opacity-0 transition-all"
          >
            <ArrowBack className="h-6 w-6 text-gray-600" />
          </button>
          <button onClick={handleBackToList}>
            <img
              src={Logo}
              alt="COMMA logo"
              className="w-85 duration-400 h-20 transition-all ease-in-out group-hover:ml-14"
              style={{
                transition: "margin-left 0.4s cubic-bezier(0.4,0,0.2,1)",
              }}
            />
          </button>
        </div>
        <div className="flex items-center space-x-4">
          <Avatar
            src={`https://api.dicebear.com/7.x/initials/svg?seed=${doctorInfo.firstName + " " + doctorInfo.lastName}`}
            alt={doctorInfo.firstName + " " + doctorInfo.lastName}
            sx={{ width: 50, height: 50 }} // MUI uses pixels
          />
          <span className="text-lg font-bold text-gray-700">
            Dr. {doctorInfo?.firstName} {doctorInfo?.lastName}
          </span>
          <button onClick={handleLogout}>
            <LogOut className="h-6 w-6 text-gray-600" />
          </button>
          {/* Animated Back Button */}
          <button
            onClick={handleBackToList}
            className="duration-400 pointer-events-none z-10 -translate-x-14 transform opacity-0 transition-all"
          >
            <ArrowBack className="h-6 w-6 text-gray-600" />
          </button>
        </div>
      </nav>

      {/* Main Content */}
      <main className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="font-semibold text-gray-800">My Patients</h1>
          <div className="flex gap-2">
            <CommaPatientImport
              doctorId={doctorInfo?.id}
              onImportComplete={refreshPatients}
            />
            <button
              onClick={handleAddPatient}
              className="rounded-lg bg-[#2A6EC2] px-4 py-2 text-white hover:bg-blue-600"
            >
              + Add Patient
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 transform text-gray-400" />
            <input
              type="text"
              placeholder="Search patients..."
              value={searchTerm}
              onChange={handleSearch}
              className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-12 pr-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Patients Table */}
        <div className="overflow-hidden rounded-lg bg-white shadow">
          <table className="min-w-full">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">
                  Patient
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">
                  Age
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">
                  Last Assessment
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredPatients.map((patient) => (
                <tr key={patient.patientId}>
                  <td className="px-6 py-4 text-left text-gray-700">
                    <div className="flex items-center space-x-4">
                      <Avatar
                        src={`https://api.dicebear.com/7.x/initials/svg?seed=${patient.fullName}`}
                        alt={patient.fullName}
                        sx={{ width: 40, height: 40 }} // MUI uses pixels
                      />
                      <div>
                        <div className="font-medium text-gray-800">
                          {patient.fullName}
                        </div>
                        <div className="text-sm text-gray-500">
                          #{String(patient.patientId).padStart(5, "0")}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-left text-gray-700">
                    {patient.age}
                  </td>
                  <td className="px-6 py-4 text-left text-gray-700">
                    {patient.lastAssessment
                      ? new Date(patient.lastAssessment).toLocaleDateString()
                      : "No assessments yet"}
                  </td>
                  <td className="px-6 py-4 text-left">
                    <button
                      onClick={() => handleViewProfile(patient.patientId)}
                      className="text-[#2A6EC2] hover:underline"
                    >
                      View Profile
                    </button>
                  </td>
                </tr>
              ))}
              {filteredPatients.length === 0 && (
                <tr>
                  <td
                    colSpan={4}
                    className="px-6 py-4 text-center text-gray-500"
                  >
                    No patients found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </main>

      {/* Add Patient Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New Patient</DialogTitle>
        <DialogContent>
          <TextField
            margin="normal"
            label="First Name"
            fullWidth
            value={newPatient.firstName}
            onChange={(e) =>
              setNewPatient({ ...newPatient, firstName: e.target.value })
            }
            required
          />
          <TextField
            margin="normal"
            label="Last Name"
            fullWidth
            value={newPatient.lastName}
            onChange={(e) =>
              setNewPatient({ ...newPatient, lastName: e.target.value })
            }
            required
          />
          <TextField
            margin="normal"
            label="Ethnicity"
            fullWidth
            value={newPatient.ethnicity}
            onChange={(e) =>
              setNewPatient({ ...newPatient, ethnicity: e.target.value })
            }
            required
          />
          <TextField
            margin="normal"
            label="Gender"
            select
            fullWidth
            value={newPatient.gender}
            onChange={(e) =>
              setNewPatient({ ...newPatient, gender: e.target.value })
            }
            required
          >
            <MenuItem value="M">Male</MenuItem>
            <MenuItem value="F">Female</MenuItem>
            <MenuItem value="Other">Other</MenuItem>
          </TextField>
          <TextField
            margin="normal"
            label="Date of Birth"
            type="date"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={newPatient.dateOfBirth}
            onChange={(e) =>
              setNewPatient({ ...newPatient, dateOfBirth: e.target.value })
            }
            required
          />
          <TextField
            margin="normal"
            label="Email"
            type="email"
            fullWidth
            value={newPatient.email}
            onChange={(e) =>
              setNewPatient({ ...newPatient, email: e.target.value })
            }
            required
          />
          <TextField
            margin="normal"
            label="Phone Number (optional)"
            type="tel"
            fullWidth
            value={newPatient.phoneNumber}
            onChange={(e) =>
              setNewPatient({ ...newPatient, phoneNumber: e.target.value })
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmitPatient}
            variant="contained"
            color="primary"
            disabled={
              !(
                newPatient.firstName &&
                newPatient.lastName &&
                newPatient.ethnicity &&
                newPatient.gender &&
                newPatient.dateOfBirth &&
                newPatient.email
              )
            }
          >
            Add Patient
          </Button>
        </DialogActions>
      </Dialog>

      {/* Alert Snackbar */}
      <Snackbar
        open={alertState.open}
        autoHideDuration={6000}
        onClose={() => setAlertState({ ...alertState, open: false })}
      >
        <Alert severity={alertState.severity} sx={{ width: "100%" }}>
          {alertState.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default SpeechDashboard;
